import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';
import Header from './Header';
import Footer from './Footer';

const ForgotPasswordPage: React.FC = () => {
  const { session, isLoaded } = useSession();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Already signed in? No need to reset — send them to Account.
  if (isLoaded && session) {
    return <Navigate to="/account" replace />;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      // The link in the email returns the user here; Supabase appends the
      // recovery token, which the client exchanges for a session on load.
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    if (resetError) {
      setError(resetError.message);
      return;
    }
    setSentTo(email);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        {sentTo ? (
          <div className="card w-full max-w-md p-8 text-center space-y-3">
            <h1 className="font-display text-3xl text-charcoal">Check your email</h1>
            <p className="text-sm text-charcoal-light">
              If an account exists for <strong>{sentTo}</strong>, we sent a link to
              reset your password. Open it to choose a new one.
            </p>
            <Link to="/sign-in" className="text-amber hover:underline text-sm">
              Back to sign in
            </Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="card w-full max-w-md p-8 space-y-5"
            aria-labelledby="forgot-heading"
          >
            <h1
              id="forgot-heading"
              className="font-display text-3xl text-charcoal mb-1"
            >
              Reset password
            </h1>
            <p className="text-sm text-charcoal-light">
              Enter your email and we&rsquo;ll send you a link to set a new password.
            </p>

            <label className="block text-sm">
              <span className="text-charcoal-light">Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
              />
            </label>

            {error && (
              <p className="text-sm text-terracotta" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>

            <p className="text-sm text-charcoal-light text-center">
              Remembered it?{' '}
              <Link to="/sign-in" className="text-amber hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
